import { useEffect, useRef, useState } from "react";
import { Scale, FolderOpen, Handshake, FileX, Landmark } from "lucide-react";
import { ParallaxImage, ParallaxText, ScrollFadeIn } from "@/components/Parallax";

const SERVICIOS = [
  {
    id: "cobranza-judicial",
    icon: Scale,
    title: "Cobranza judicial integral",
    description: "Juicios ejecutivos, procedimientos especiales, acciones hipotecarias, desposeimientos, tercerías y demás gestiones judiciales necesarias para la recuperación de créditos."
  },
  {
    id: "gestion-carteras",
    icon: FolderOpen,
    title: "Gestión de carteras judiciales",
    description: "Administración, seguimiento y control de carteras morosas, con foco en orden, trazabilidad y eficiencia."
  },
  {
    id: "negociacion",
    icon: Handshake,
    title: "Negociación y recupero",
    description: "Apoyo en acuerdos de pago, renegociaciones, daciones en pago y otras soluciones jurídicas orientadas al recupero."
  },
  {
    id: "incobrabilidad",
    icon: FileX,
    title: "Incobrabilidades y cierre fundado",
    description: "Gestión de casos sin viabilidad de recupero, cierres fundados de cobranza y emisión de certificados de incobrabilidad."
  },
  {
    id: "concursales",
    icon: Landmark,
    title: "Procedimientos Concursales",
    description: "Especialistas en representación de acreedores en procedimientos concursales."
  }
];

export default function ServiciosSection() {
  return (
    <section id="servicios" data-testid="servicios-section" className="py-20 md:py-28 lg:py-32 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header with image */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          <ParallaxText speed={-0.15} className="relative z-10">
            <ScrollFadeIn direction="left" delay={0}>
              <p className="text-calle-primary-50 text-xs uppercase tracking-[0.3em] mb-4 font-semibold font-body">
                Soluciones jurídicas para créditos morosos
              </p>
            </ScrollFadeIn>
            <ScrollFadeIn direction="left" delay={0.1}>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-calle-primary leading-[1.1] tracking-[-0.01em]">
                Nos especializamos en recupero judicial
              </h2>
            </ScrollFadeIn>
            <ScrollFadeIn direction="left" delay={0.2}>
              <div className="calle-underline mt-6"></div>
            </ScrollFadeIn>
          </ParallaxText>
          
          <ScrollFadeIn direction="right" delay={0.2}>
            <ParallaxImage
              src="/images/estanteria.png"
              alt="Biblioteca jurídica"
              className="grayscale-img"
              containerClassName="w-full h-[300px] lg:h-[400px] rounded-sm shadow-2xl"
              speed={0.6}
            />
          </ScrollFadeIn>
        </div>

        {/* Service cards - First row: 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-6 lg:mb-8">
          {SERVICIOS.slice(0, 3).map((servicio, index) => (
            <ScrollFadeIn 
              key={servicio.id} 
              direction="up"
              delay={index * 0.1}
            >
              <ParallaxText speed={-0.03 * (index + 1)}>
                <div 
                  data-testid={`servicio-${servicio.id}`}
                  className="bg-white p-6 lg:p-8 rounded-sm border border-gray-200 hover:border-calle-primary/30 transition-all duration-500 hover:shadow-xl group h-full min-h-[260px] flex flex-col hover:-translate-y-2"
                >
                  <div className="w-12 h-12 rounded-sm bg-gray-100 flex items-center justify-center mb-5 group-hover:bg-calle-primary group-hover:scale-110 transition-all duration-500">
                    <servicio.icon className="h-6 w-6 text-calle-primary group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="font-heading text-lg lg:text-xl text-calle-primary mb-3">
                    {servicio.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed font-body flex-grow">
                    {servicio.description}
                  </p>
                </div>
              </ParallaxText>
            </ScrollFadeIn>
          ))}
        </div>

        {/* Service cards - Second row: 2 cards centered */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {SERVICIOS.slice(3, 5).map((servicio, index) => (
            <ScrollFadeIn 
              key={servicio.id} 
              direction="up"
              delay={(index + 3) * 0.1}
            >
              <ParallaxText speed={-0.03 * (index + 4)}>
                <div 
                  data-testid={`servicio-${servicio.id}`}
                  className="bg-white p-6 lg:p-8 rounded-sm border border-gray-200 hover:border-calle-primary/30 transition-all duration-500 hover:shadow-xl group h-full min-h-[260px] flex flex-col hover:-translate-y-2"
                >
                  <div className="w-12 h-12 rounded-sm bg-gray-100 flex items-center justify-center mb-5 group-hover:bg-calle-primary group-hover:scale-110 transition-all duration-500">
                    <servicio.icon className="h-6 w-6 text-calle-primary group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="font-heading text-lg lg:text-xl text-calle-primary mb-3">
                    {servicio.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed font-body flex-grow">
                    {servicio.description}
                  </p>
                </div>
              </ParallaxText>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
